import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Bot, Loader2 } from "lucide-react";
import { social } from "../mock";

// Empty when no backend is configured: requests then go to a relative /api
// path and fail cleanly into the existing error handling, instead of
// hitting "undefined/api".
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

function sessionId() {
  let id = localStorage.getItem("kse_chat_session");
  if (!id) {
    id = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("kse_chat_session", id);
  }
  return id;
}

const SUGGESTIONS = [
  "What products do you manufacture?",
  "What is your monthly capacity?",
  "Are you certified?",
  "How can I place an order?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the KSE Assistant. Ask me anything about our products, capabilities or how to get in touch." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  // Allow the search bar (or other components) to open chat & ask a question.
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const q = e.detail;
      if (q) setTimeout(() => send(q), 300);
    };
    window.addEventListener("kse-ask", handler);
    return () => window.removeEventListener("kse-ask", handler);
    // eslint-disable-next-line
  }, []);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId(), message: msg });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble. Please chat with us on WhatsApp." }]);
    } finally {
      setLoading(false);
    }
  };

  const waHref = `https://wa.me/${social.whatsapp}?text=${encodeURIComponent("Hello K.S.E. Inc, I have a question.")}`;

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Open chat assistant"
        className="fixed bottom-6 left-6 z-[90] w-14 h-14 rounded-full bg-[#1a2a6b] hover:bg-[#2b3aa0] text-white flex items-center justify-center shadow-2xl transition-all"
      >
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>

      {open && (
        <div className="fixed bottom-24 left-6 z-[91] w-[calc(100vw-3rem)] sm:w-[380px] h-[520px] max-h-[75vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="bg-[#1a2a6b] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
              <Bot size={22} />
            </div>
            <div className="flex-1">
              <div className="font-bold leading-tight">KSE Assistant</div>
              <div className="text-blue-200 text-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Online now
              </div>
            </div>
            <a href={waHref} target="_blank" rel="noopener noreferrer" className="text-xs bg-[#25D366] px-3 py-1.5 rounded-full font-semibold">
              WhatsApp
            </a>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#2b3aa0] text-white rounded-br-md"
                      : "bg-white text-slate-700 border border-slate-100 rounded-bl-md shadow-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <Loader2 size={16} className="animate-spin text-[#2b3aa0]" />
                </div>
              </div>
            )}

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-xs bg-white border border-slate-200 hover:border-[#2b3aa0] text-[#1a2a6b] px-3 py-1.5 rounded-full transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-slate-100 flex items-center gap-2 bg-white"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2b3aa0]/40 focus:border-[#2b3aa0]"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-10 h-10 rounded-full bg-[#2b3aa0] hover:bg-[#1a2a6b] text-white flex items-center justify-center shrink-0 disabled:opacity-60"
            >
              <Send size={17} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
